import React from "react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Phone, Mail, MessageSquare, ArrowRight, Zap, Star, Clock } from "lucide-react";

const CTASection = () => {
  const benefits = [
    {
      icon: Zap,
      title: "Respons Cepat",
      description: "Tim kami siap merespons dalam 24 jam"
    },
    {
      icon: Star,
      title: "Kualitas Terjamin",
      description: "Standar internasional untuk setiap project"
    },
    {
      icon: Clock,
      title: "Tepat Waktu",
      description: "Komitmen penyelesaian sesuai timeline"
    }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section className="py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-orange-600 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{backgroundImage: "url('data:image/svg+xml,%3Csvg width=%2260%22 height=%2260%22 viewBox=%220 0 60 60%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cg fill=%22none%22 fill-rule=%22evenodd%22%3E%3Cg fill=%22%23ffffff%22 fill-opacity=%220.1%22%3E%3Ccircle cx=%2230%22 cy=%2230%22 r=%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')"}}></div>
      </div>

      {/* Floating Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-32 h-32 bg-white/5 rounded-full animate-float"></div>
        <div className="absolute top-40 right-20 w-24 h-24 bg-orange-300/10 rounded-full animate-pulse-soft"></div>
        <div className="absolute bottom-20 left-1/4 w-40 h-40 bg-blue-300/5 rounded-full animate-float"></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Main CTA */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Siap Meningkatkan
              <span className="block">Brand & Digital Presence Anda?</span>
            </h2>
            <p className="text-xl md:text-2xl text-blue-100 mb-10 max-w-3xl mx-auto leading-relaxed">
              Mari wujudkan visi kreatif Anda bersama tim profesional RanselPro. 
              Dari konsep hingga eksekusi, kami siap membantu mencapai tujuan bisnis Anda.
            </p>

            {/* Primary CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-12">
              <Button
                size="lg"
                onClick={() => scrollToSection("contact")}
                className="bg-white text-blue-600 hover:bg-blue-50 px-10 py-6 text-xl font-bold rounded-2xl shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300"
              >
                <Phone className="w-6 h-6 mr-3" />
                Hubungi Kami Sekarang
              </Button>
              
              <Button
                size="lg"
                variant="outline"
                onClick={() => scrollToSection("portfolio")}
                className="border-2 border-white/30 text-white hover:bg-white/10 backdrop-blur-md px-10 py-6 text-xl font-bold rounded-2xl hover:border-white/50 transition-all duration-300"
              >
                Lihat Portfolio
                <ArrowRight className="w-6 h-6 ml-3" />
              </Button>
            </div>
          </div>

          {/* Benefits Cards */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {benefits.map((benefit, index) => (
              <Card key={index} className="bg-white/10 backdrop-blur-md border-white/20 hover:bg-white/20 transition-all duration-300 card-hover">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <benefit.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{benefit.title}</h3>
                  <p className="text-blue-100 leading-relaxed">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Contact Methods */}
          <div className="bg-white/10 backdrop-blur-md rounded-3xl border border-white/20 p-8 md:p-12">
            <div className="text-center mb-10">
              <h3 className="text-3xl font-bold text-white mb-4">
                Mulai Project Anda Hari Ini
              </h3>
              <p className="text-blue-100 text-lg">
                Pilih cara yang paling nyaman untuk menghubungi kami
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {/* WhatsApp */}
              <div className="text-center group">
                <div className="w-20 h-20 bg-green-500 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                  <MessageSquare className="w-10 h-10 text-white" />
                </div>
                <h4 className="text-xl font-bold text-white mb-2">WhatsApp</h4>
                <p className="text-blue-100 mb-4">Chat langsung dengan tim kami</p>
                <Button
                  className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-xl"
                  onClick={() => window.open("https://wa.me/6285133079588", "_blank")}
                >
                  Chat Sekarang
                </Button>
              </div>

              {/* Email */}
              <div className="text-center group">
                <div className="w-20 h-20 bg-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Mail className="w-10 h-10 text-white" />
                </div>
                <h4 className="text-xl font-bold text-white mb-2">Email</h4>
                <p className="text-blue-100 mb-4">Kirim detail project Anda</p>
                <Button
                  className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-xl"
                  onClick={() => window.open("mailto:ranselproduction@gmail.com", "_blank")}
                >
                  Kirim Email
                </Button>
              </div>

              {/* Phone */}
              <div className="text-center group">
                <div className="w-20 h-20 bg-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Phone className="w-10 h-10 text-white" />
                </div>
                <h4 className="text-xl font-bold text-white mb-2">Telepon</h4>
                <p className="text-blue-100 mb-4">Diskusi langsung dengan expert</p>
                <Button
                  className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-xl"
                  onClick={() => window.open("tel:+6281234567890", "_blank")}
                >
                  Telepon Kami
                </Button>
              </div>
            </div>
          </div>

          {/* Urgency Element */}
          <div className="mt-12 text-center">
            <div className="inline-flex items-center px-6 py-3 bg-orange-500 rounded-full text-white font-semibold text-lg animate-pulse">
              <Star className="w-5 h-5 mr-2" />
              Free Konsultasi Terbatas - Hanya 10 Slot Tersisa!
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;